// import {
//     // splAxios,
//     // splAxiosBare,
//     splAxiosV1,
// } from '../api/spl-axios';
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';

// RESOURCE-STYLE ENDPOINTS
// starting from scratch...
// -------------------------------------------------------------

// if you add any here, make sure you make _handler for them in class below
const standardResourceEndpointActions = [ 
    'list',
    'retrieve',
    'create',
    'update',
    'partialUpdate',
    'remove', // soft delete - for isActiveModel subclasses
    'destroy', 
    'head', 
    'options',
    'getSchema',
];

export default class ResourceViewsetEndpoint {
    constructor({
        resourceName,  // name of resource used on frontend (and should be backend)
        generalUrl,
        enabledActions,  // array of standard actions that you want ENABLED - if you omit this altogether from constructor params, then all will be enabled
        disabledActions,  // array of standard actions you want to DISABLE - THIS WINS IF YOU HAVE IN BOTH enabled and disabled
        axiosOptions,  // optional object to pass to axios.create() (merge with our defaults)
    }) {
        this.resourceName = resourceName;

        // CREATE AXIOS INSTANCE
        // each endpoint will have its own axios instance, 
        // so it can manage its baseURL, any special cookies, headers, etc.
        const axiosDefaults = {
            baseURL: '/api/v1/',
            xsrfCookieName: window?.splData?.CSRF_COOKIE_NAME || 'csrftoken',
            xsrfHeaderName: 'X-CSRFTOKEN',
            headers: {...window?.splFixedHeaders || {}}
        };
        const extraAxiosOptions = axiosOptions ? cloneDeep(axiosOptions) : {};
        // probably don't need that cloneDeep... but paranoid about mutating things across instances.... and this shouldn't get called often...
        const finalAxiosOptions = Object.assign({}, axiosDefaults, extraAxiosOptions);
        this.axios = axios.create(finalAxiosOptions);

        // FIGURE OUT ENABLED ACTIONS
        // you can set enabledActions and disabledActions - disabled wins if in both
        const enabled = (!!enabledActions && Array.isArray(enabledActions)) ? [...enabledActions] : [...standardResourceEndpointActions];
        if (disabledActions && Array.isArray(disabledActions)) {
            // if you have disabledActions, filter them out of 'enabled' then set final list
            this.enabledActions = enabled.filter(act => !disabledActions.includes(act));
        } else {
            this.enabledActions = enabled;
        }

        // URLs
        this.generalUrl = generalUrl;  // generalUrl is the URL for this object, with trailing slash

        // SET ENABLED ACTION HANDLERS 
        // run through enabledActions and 'connect' to handler methods - or disabled action handler if not enabled
        // OR... maybe have all handlers created by default, always return a promise, but just return a dummy one if not enabled?
        //      that is better for typing/editor support 

        // function for constructing disabled action handlers - function that returns a function
        const makeDisabledActionHandler = (actionName) => {
            const handler = () => {  // this is the actual handler function that gets called if you try to use this action
                let errMsg = `This action (${actionName}) is not implemented on this endpoint (${this.resourceName}) (message generated by disabled action handler)`;
                console.error(errMsg);
                return Promise.reject(errMsg);  // return a promise, to match what axios would return
            };
            return handler;
        };

        standardResourceEndpointActions.forEach(actionName => {
            if (this.enabledActions.includes(actionName)) {
                const actionHandlerName = `_${actionName}`;  // add the underscore
                this[actionName] = this[actionHandlerName];
                // or should you return a function that returns the handler???
            } else {
                // not enabled?  hook up to our 'disabledAction' handler - we construct a new handler for each disabledAction
                this[actionName] = makeDisabledActionHandler(actionName);
            }
        })
    }
    objectUrl(id) {
        // return the DETAIL URL for a specific resource instance
        // has resource ID + trailing slash:  like rfxs/6/
        return this.generalUrl + id.toString() + '/';
    }
    // ACTION HANDLERS - 'hook up' if they are in 'enabledActions'
    _list(config) {
        return this.axios.get(this.generalUrl, config);
    }
    _listRelated(id, relatedUrl, config) {
        // pass a string of a child/related URL to get related/child objects, using D-REST ViewSet
        // like for product attachments, use product endpoint and pass 'attachments/' as relatedUrl
        return this.axios.get(this.objectUrl(id) + relatedUrl, config);
    }
    _retrieve(id, config) {
        return this.axios.get(this.objectUrl(id), config);
    }
    _create(data, config) {
        return this.axios.post(this.generalUrl, data, config);
    }
    _update(id, data, config) {
        return this.axios.put(this.objectUrl(id), data, config);
    }
    _partialUpdate(id, data, config) {
        return this.axios.patch(this.objectUrl(id), data, config);
    }
    _remove(id, config) {
        // ONLY use for things with is_active flag - this means you have to keep entire partial_update method open.. instead, you should implement a custom @action that ONLY does soft delete, and call that action from this
        return this.axios.patch(this.objectUrl(id), {is_active: false}, config);
    }
    _destroy(id, config) {
        return this.axios.delete(this.objectUrl(id), config);
    }
    _head(config) {
        return this.axios.head(this.generalUrl, config);
    }
    _options(config) {
        return this.axios.options(this.generalUrl, config);
    }
    _getSchema(config) {
        return this.axios.get(this.generalUrl + 'get_schema/', config);
    }
    // HANDLER TO CALL WHEN AN ACTION IS DISABLED
    _disabledAction(actionName) {
        let errMsg = `This action (${actionName}) is not implemented on this endpoint (${this.resourceName})`;
        console.error(errMsg);
        return Promise.reject(errMsg);  // return a promise, to match what axios would return
    }
}
